import { TrashIcon } from '@heroicons/react/24/outline';
import { startCase } from 'lodash-es';

import { useCartContext } from '~/components/Cart/CartDrawerContext';
import ConduitHealthLoader from '~/components/Loader/ConduitHealthLoader';
import { useUpdateCart } from '~/hooks/useCart';
import { useToast } from '~/hooks/useToast';
import { type CartItem, removeItem } from '~/types/cart';
import { productImageFromImages } from '~/utils/productImage';

type Props = {
  item: CartItem;
};

export default function CartItem({ item }: Props) {
  const { toast, errorToast } = useToast();
  const { cart } = useCartContext();

  const remove = useUpdateCart({
    cart: removeItem(cart, item.product.id),
    onSuccess: () => toast({ title: 'Item removed from cart', variant: 'dismissPrimary' }),
    onError: (error) => errorToast({ error, title: 'Problem removing item from cart' }),
  });

  return (
    <div className="grid grid-cols-[auto_1fr_auto] items-start gap-x-5 rounded-lg bg-gray-50 px-5 py-6">
      <img
        className="aspect-square h-20 w-20 object-cover"
        src={productImageFromImages(item.product.images)}
        alt={item.product.title}
      />

      <div>
        <h3 className="mb-3 text-xl font-semibold">{item.product.title}</h3>
        <div className="flex flex-col items-start space-y-3">
          {item.product.properties.map((property) => (
            <div
              key={property.key}
              className="rounded-md bg-gray-200 px-[10px] py-[2px] text-xs font-medium"
            >
              {property.displayText}:{' '}
              {startCase(
                item.selectedProperties.find((p) => p.key === property.key)?.value ?? 'TBD'
              )}
            </div>
          ))}
        </div>
      </div>

      <button type="button" onClick={() => remove.mutate()} className="p-0.5">
        {remove.isPending ? (
          <ConduitHealthLoader className="size-6" />
        ) : (
          <TrashIcon strokeWidth={2} className="size-6 text-gray-500" />
        )}
      </button>
    </div>
  );
}
